import { apiHttpClient } from '@/http/httpClient';
import type { PaginationItems } from '@/models/pagination';
import type { Transport } from '@/models/transport/transport';
import type { TransportDetailModel } from '@/models/transport/transport-detail';
import type { CalculateRouteResponse, TrajectoryOptimizationResponse, TransportAction } from '@/models/transport/transport-action';
import type { TransportRoutingModel } from '@/models/transport/routing';
import type { TransportListModel, TransportListRequest } from '@/models/transport/transport-list';
import type { TransportTrajectoryListModel, TransportTrajectoryListRequest } from '@/models/transport/trajectory-list';
import type { TransportGeneralInformation } from '@/models/transport/edit/general-information';
import type { TransportVehicleEditInformation } from '@/models/transport/edit/transport-vehicle-edit-information';
import type { UpdateStartOrEndLocationRequest } from '@/models/transport/edit/update-start-or-end-location-request';
import { extractFileNameFromHeaders } from '@/helpers/http-headers-helper';
import type { TransportHistoryModel } from '@/models/transport/transport-history';
import type { AbortTrajectoryResolution } from '@/constants/abort-trajectory-resolution';

const ENDPOINT_LIST_TRANSPORTS = 'Transport/List';
const ENDPOINT_GET_TRANSPORT = 'Transport/Get';
const ENDPOINT_DOWNLOAD_ROADMAP = 'Transport/DownloadRoadmap';
const ENDPOINT_DELETE_TRANSPORT = 'Transport/Delete';
const ENDPOINT_CREATE_TRANSPORT = 'Transport/Create';
const ENDPOINT_UPDATE_TRANSPORT = 'Transport/Update';
const ENDPOINT_UPDATE_TRANSPORT_GENERAL_INFO = 'Transport/UpdateGeneralInformation';
const ENDPOINT_UPDATE_TRANSPORT_TRANSPORT_VEHICLE_INFO = 'Transport/UpdateTransportVehicleInformation';
const ENDPOINT_GET_ROUTING = 'Transport/GetRouting';
const ENDPOINT_LIST_TRAJECTORIES = 'Transport/ListTrajectories';
const ENDPOINT_OPTIMIZE_TRANSPORT_TRAJECTORIES = 'Transport/OptimizeTrajectories';
const ENDPOINT_CALCULATE_TRANSPORT_ROUTE = 'Transport/CalculateRoute';
const ENDPOINT_CREATE_OR_UPDATE_TRANSPORT_REMARK = 'Transport/CreateOrUpdateRemark';
const ENDPOINT_DELETE_TRANSPORT_REMARK = 'Transport/DeleteRemark';
const ENDPOINT_UPDATE_TRANSPORT_START_DATE = 'Transport/UpdateTransportStartDate';
const ENDPOINT_UPDATE_START_OR_END_LOCATION = 'Transport/UpdateStartOrEndLocation';
const ENDPOINT_SET_TO_PLAN = 'Transport/SetToPlan';
const ENDPOINT_SET_PLANNED = 'Transport/SetPlanned';
const ENDPOINT_SET_PLANNED_MULTIPLE = 'Transport/SetPlannedMultiple';
const ENDPOINT_DELETE_MULTIPLE_TRANSPORTS = 'Transport/DeleteMultiple';
const ENDPOINT_WITHDRAW_ACCEPTANCE = 'Transport/WithdrawAcceptance';
const ENDPOINT_COMPLETE_TRANSPORT = 'Transport/CompleteTransport';
const ENDPOINT_ABORT_TRANSPORT = 'Transport/AbortTransport';
const ENDPOINT_GET_HISTORY = 'Transport/GetHistory';

export const listTransports = async (request: TransportListRequest) => {
  return await apiHttpClient()
    .post<PaginationItems<TransportListModel>>(ENDPOINT_LIST_TRANSPORTS, request)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const createTransport = async (request: TransportDetailModel) => {
  return await apiHttpClient()
    .post<Transport>(ENDPOINT_CREATE_TRANSPORT, request)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const updateTransport = async (request: TransportDetailModel) => {
  return await apiHttpClient()
    .post<Transport>(ENDPOINT_UPDATE_TRANSPORT, request)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const updateGeneralTransportInformation = async (request: TransportGeneralInformation) => {
  return await apiHttpClient()
    .post(ENDPOINT_UPDATE_TRANSPORT_GENERAL_INFO, request)
    .then((response) => response)
    .catch((error) => error);
};

export const updateTransportVehicleForTransport = async (request: TransportVehicleEditInformation) => {
  return await apiHttpClient()
    .post(ENDPOINT_UPDATE_TRANSPORT_TRANSPORT_VEHICLE_INFO, request)
    .then((response) => response)
    .catch((error) => error);
};

export const getTransport = async (id: number) => {
  return await apiHttpClient()
    .get<Transport>(ENDPOINT_GET_TRANSPORT, {
      params: {
        id: id,
      },
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const downloadRoadmap = async (id: number) => {
  return await apiHttpClient()
    .get<Blob>(ENDPOINT_DOWNLOAD_ROADMAP, {
      params: {
        transportId: id,
      },     
      responseType: 'blob'      
    })
    .then((response) => {
      const url = globalThis.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = extractFileNameFromHeaders(response) ?? `roadmap_${id}.pdf`;
      link.click();
      globalThis.URL.revokeObjectURL(url);

      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const deleteTransport = async (id: number) => {
  return await apiHttpClient()
    .delete<Transport>(ENDPOINT_DELETE_TRANSPORT, {
      params: {
        id: id,
      },
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const optimizeTransportTrajectories = async (id: number) => {
  return await apiHttpClient()
    .post<TrajectoryOptimizationResponse>(ENDPOINT_OPTIMIZE_TRANSPORT_TRAJECTORIES, {
      transportId: id,
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const calculateTransportRoute = async (id: number, actions: TransportAction[], baseActionIds?: number[]) => {
  return await apiHttpClient()
    .post<CalculateRouteResponse>(ENDPOINT_CALCULATE_TRANSPORT_ROUTE, {
      transportId: id,
      actions: actions,
      baseActionIds: baseActionIds ?? null,
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getRouting = async (id: number) => {
  return await apiHttpClient()
    .get<TransportRoutingModel>(ENDPOINT_GET_ROUTING, {
      params: {
        transportId: id,
      },
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const listTrajectories = async (request: TransportTrajectoryListRequest, transportId?: number, onlyAvailableTrajectories = false) => {
  return await apiHttpClient()
    .post<PaginationItems<TransportTrajectoryListModel>>(ENDPOINT_LIST_TRAJECTORIES, {
      ...request,
      transportId: transportId,
      onlyAvailableTrajectories: onlyAvailableTrajectories,
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const createOrUpdateTransportRemark = async (transportId: number, remark: string) => {
  return await apiHttpClient()
    .post(`${ENDPOINT_CREATE_OR_UPDATE_TRANSPORT_REMARK}`, { transportId, remark })
    .then((response) => response)
    .catch((error) => error);
};

export const deleteTransportRemark = async (transportId: number) => {
  return await apiHttpClient()
    .delete(`${ENDPOINT_DELETE_TRANSPORT_REMARK}/${transportId}`)
    .then((response) => response)
    .catch((error) => error);
};

export const updateTransportStartDate = async (transportId: number, transportStartDate: number) => {
  return await apiHttpClient()
    .post(ENDPOINT_UPDATE_TRANSPORT_START_DATE, { transportId, transportStartDate })
    .then((response) => response)
    .catch((error) => error);
};

export const updateStartOrEndLocation = async (request: UpdateStartOrEndLocationRequest) => {
  return await apiHttpClient()
    .put(ENDPOINT_UPDATE_START_OR_END_LOCATION, request)
    .then((response) => response)
    .catch((error) => error);
};

export const setToPlan = async (transportId: number) => {
  return await apiHttpClient()
    .post(ENDPOINT_SET_TO_PLAN, { TransportId: transportId })
    .then((response) => response)
    .catch((error) => error);
};

/**
 * @param confirmEstimatedDistances Of de planner heeft bevestigd dat hij deze rit ook wil plannen met deels
 *   geschatte afstand en rijtijd (profiel Q uit werkitem 7375). Zonder bevestiging weigert de validator het
 *   verzoek met TRANSPORT_HAS_ESTIMATED_DISTANCES.
 */
export const setPlanned = async (transportId: number, confirmEstimatedDistances = false) => {
  return await apiHttpClient()
    .post(ENDPOINT_SET_PLANNED, {
      TransportId: transportId,
      ConfirmEstimatedDistances: confirmEstimatedDistances,
    })
    .then((response) => response)
    .catch((error) => error);
};

/**
 * @param confirmEstimatedDistances Of de planner heeft bevestigd dat hij ook ritten wil plannen waarvan afstand
 *   en rijtijd deels geschat zijn (profiel Q uit werkitem 7375). Zonder bevestiging laat de backend die ritten
 *   staan en meldt hij ze apart terug.
 */
export const setPlannedMultiple = async (transportIds: number[], confirmEstimatedDistances = false) => {
  return await apiHttpClient()
    .post(ENDPOINT_SET_PLANNED_MULTIPLE, {
      TransportIds: transportIds,
      ConfirmEstimatedDistances: confirmEstimatedDistances,
    })
    .then((response) => response)
    .catch((error) => error);
};

export const deleteMultipleTransports = async (transportIds: number[]) => {
  return await apiHttpClient()
    .post(ENDPOINT_DELETE_MULTIPLE_TRANSPORTS, { TransportIds: transportIds })
    .then((response) => response)
    .catch((error) => error);
};

export const withdrawAcceptance = async (transportId: number) => {
  return await apiHttpClient()
    .post(ENDPOINT_WITHDRAW_ACCEPTANCE, { TransportId: transportId })
    .then((response) => response)
    .catch((error) => error);
};

export const completeTransport = async (transportId: number) => {
  return await apiHttpClient()
    .post(ENDPOINT_COMPLETE_TRANSPORT, { TransportId: transportId })
    .then((response) => response)
    .catch((error) => error);
};

export interface AbortTrajectoryChoice {
  trajectoryId: number;
  resolution: AbortTrajectoryResolution;
  transferRelationId: number | null;
  transferAddressId: number | null;
}

export const abortTransport = async (transportId: number, choices: AbortTrajectoryChoice[]) => {
  return await apiHttpClient()
    .post(ENDPOINT_ABORT_TRANSPORT, { TransportId: transportId, Choices: choices })
    .then((response) => response)
    .catch((error) => error);
};

export const getTransportHistory = async (transportId: number) => {
  return await apiHttpClient()
    .get<TransportHistoryModel[]>(ENDPOINT_GET_HISTORY, { params: { transportId } })
    .then((response) => response)
    .catch((error) => error);
};